import type {
  FormState,
  FormStateValue,
} from '@de/providers/DataSourceManager.types';
import { formStateValuesEqual } from '@de/utils/form-state';
import { withEndpointExcludedFormKeys } from '@de/utils/data-sources/endpointExcludedFormKeys';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import {
  extractPaths,
  hasTemplate,
  hasTemplateDeep,
  parse,
  type Expression,
} from '@de/utils/interpolation';
import {
  createDataSourceCache,
  stableStringify,
  type DataSourceCache,
} from '@de/utils/data-sources/dataSourceCache';
import { dispatchFetchDataSource } from '@de/utils/data-sources/dispatchFetchDataSource';
import type { InterpolatedRequest } from '@de/utils/data-sources/fetchDataSource';
import { deriveCacheKey } from '@de/utils/data-sources/dataSourceCache';
import { deriveCacheKeyForDecl } from '@de/utils/data-sources/deriveCacheKeyForDecl';
import {
  getDataSourceKind,
  getDeclarationInterpolatableFields,
} from '@de/utils/data-sources/data-source-kind';
import { createKindGate } from '@de/utils/data-sources/kind-concurrency';
import { injectFlowTypeIntoOfferingsRequest } from '@de/utils/data-sources/injectFlowTypeIntoOfferingsRequest';
import { injectTrafficSourceIntoOfferingsRequest } from '@de/utils/data-sources/injectTrafficSourceIntoOfferingsRequest';
import { injectPresentationUrlIntoOfferingsRequest } from '@de/utils/data-sources/injectPresentationUrlIntoOfferingsRequest';
import getTrafficSource from '@de/utils/getTrafficSource';
import { cascadeSourceNames } from '@de/utils/data-sources/cascadeSourceNames';
import { dependencyClosure } from '@de/utils/data-sources/dependencyClosure';
import { dependencyLevels } from '@de/utils/data-sources/dependencyLevels';
import { hasUnresolvedDependency } from '@de/utils/data-sources/hasUnresolvedDependency';
import {
  fetchWhenReadsFormKey,
  fetchWhenRefusal,
  fetchWhenSatisfied,
} from '@de/utils/data-sources/fetchWhen';
import { assignDerivedRoots } from '@de/utils/data-sources/derivedRoots';
import { warnOnce } from '@de/utils/warnOnce';
import {
  declarationReadsBoundFormKey,
  reportingSourceMustWait,
} from '@de/utils/data-sources/interpolateReportingBody';
import {
  executionDependencyNames,
  readyWhenSatisfied,
} from '@de/utils/data-sources/readyWhen';
import { topologicalSort } from '@de/utils/data-sources/topologicalSort';
import {
  findAbsentTopologyBlockId,
  TopologyBlockAbsentError,
} from '@de/utils/topology/absent-topology-block';
import {
  projectCanvasTopology,
  type TopologyProjection,
} from '@de/utils/topology/project-canvas-topology';
import { resolveDataSourceType } from '@de/utils/data-sources/source-types';
import type { DataSourceHostConfig } from '@de/utils/data-sources/source-type-registry';
import {
  DataSourceMissingDependencyError,
  DataSourcePreconditionRefusedError,
  type DataSourceDeclaration,
  type DataSourceManagerContextValue,
  type DataSourceManagerProps,
  type ReportingModelDataSourceDeclaration,
  type ResolvedDataSource,
} from './DataSourceManager.types';
import { useOptionalPublishedScope } from './PublishedScopeProvider';
import { markPublishedScopeContext } from '../utils/published-scope/unpublishedScope';
import { scopeContentSignature } from '../utils/published-scope/scopeContentSignature';
import { markInjectedRouteContext } from '../utils/interpolation/injectedRouteMarker';
import { useRoute, type CeRoute } from './RouteProvider';
import { useServerResolvedSources } from './ServerResolvedSources';
import {
  identityFingerprint,
  readServerResolvedSources,
  type ServerResolvedSource,
} from '@de/utils/data-sources/server/serverSeed';

export const DataSourceManagerContext = createContext<
  DataSourceManagerContextValue | undefined
>(undefined);

/**
 * Default in-flight cap for {@link DataSourceManagerContextValue.refetchMany}.
 * Chosen to shorten a many-source refresh without handing the reporting backend
 * an unbounded burst.
 */
const DEFAULT_REFETCH_CONCURRENCY = 5;

/**
 * Names one in-flight walk has not finished. Each walk owns its set. A route
 * or scope bump unions every set still here. A walk removes only its own
 * entry when it finishes.
 */
interface InflightWalk {
  readonly generation: number;
  readonly names: Set<string>;
}

type SourcesState = Readonly<Record<string, ResolvedDataSource>>;

type SourceAction =
  | {
      readonly type: 'reset';
      readonly names: readonly string[];
      readonly missingBySource: ReadonlyMap<string, string>;
      /** Names the server resolved; they keep the data they started with. */
      readonly seeded?: ReadonlySet<string>;
    }
  | {
      readonly type: 'reconcile';
      readonly names: readonly string[];
      readonly refetch: ReadonlySet<string>;
      readonly missingBySource: ReadonlyMap<string, string>;
    }
  | { readonly type: 'pending'; readonly name: string }
  | { readonly type: 'skipped'; readonly name: string }
  | { readonly type: 'dispatched'; readonly name: string }
  // `at` travels on the action rather than being read inside the reducer: a
  // reducer that calls Date.now() returns different state for the same input,
  // and React invokes reducers twice in development.
  | {
      readonly type: 'resolved';
      readonly name: string;
      readonly data: unknown;
      readonly at: number;
    }
  | {
      readonly type: 'empty';
      readonly name: string;
      readonly data: unknown;
      readonly at: number;
    }
  | { readonly type: 'error'; readonly name: string; readonly error: Error };

function sourcesReducer(
  state: SourcesState,
  action: SourceAction,
): SourcesState {
  switch (action.type) {
    case 'reset': {
      const next: Record<string, ResolvedDataSource> = {};
      for (const name of action.names) {
        const missing = action.missingBySource.get(name);
        if (missing !== undefined) {
          next[name] = {
            data: undefined,
            status: 'error',
            error: new DataSourceMissingDependencyError(name, missing),
          };
        } else if (action.seeded?.has(name) && state[name]) {
          next[name] = state[name];
        } else {
          next[name] = { data: undefined, status: 'pending' };
        }
      }
      return next;
    }
    case 'reconcile': {
      // Rebuild state to exactly the declared names (dropping removed ones)
      // while touching only what changed:
      //   - missing dependency  → seed `error`
      //   - added / content-changed (in `refetch`) → `pending`, its run is
      //     dispatched by the effect
      //   - unchanged           → preserve the previous snapshot verbatim,
      //     so consumers keep the same `data` reference and never see a
      //     `pending` flash for a source that did not change.
      const next: Record<string, ResolvedDataSource> = {};
      for (const name of action.names) {
        const missing = action.missingBySource.get(name);
        if (missing !== undefined) {
          next[name] = {
            data: undefined,
            status: 'error',
            error: new DataSourceMissingDependencyError(name, missing),
          };
        } else if (action.refetch.has(name)) {
          next[name] = { data: undefined, status: 'pending' };
        } else {
          next[name] = state[name] ?? { data: undefined, status: 'pending' };
        }
      }
      // Preserve state identity when the rebuild changed nothing. Unchanged
      // names reuse their previous snapshot by reference, so a shallow compare
      // is enough — and returning `state` makes React bail out of the update
      // instead of handing every consumer a new `sources` object. Without this,
      // any re-run of the reconcile effect re-renders the whole subtree, which
      // is what lets a dashboard-sized graph stack enough nested updates to
      // trip React's depth limit.
      if (Object.keys(state).length === action.names.length) {
        let identical = true;
        for (const name of action.names) {
          if (state[name] !== next[name]) {
            identical = false;
            break;
          }
        }
        if (identical) {
          return state;
        }
      }
      return next;
    }
    // A refetch in flight and a failed fetch both carry the previous
    // `lastUpdatedAt` forward. Neither changes when the data a consumer is
    // looking at was obtained, and dropping it would make a retry loop report
    // data of unknown age.
    case 'pending':
      return {
        ...state,
        [action.name]: {
          data: undefined,
          status: 'pending',
          lastUpdatedAt: state[action.name]?.lastUpdatedAt,
        },
      };
    case 'skipped': {
      const current = state[action.name];
      if (current?.status === 'pending' && current.skipped && !current.data) {
        return state;
      }
      return {
        ...state,
        [action.name]: {
          data: undefined,
          status: 'pending',
          skipped: true,
          lastUpdatedAt: current?.lastUpdatedAt,
        },
      };
    }
    case 'dispatched': {
      const current = state[action.name];
      return {
        ...state,
        [action.name]: {
          data: current?.data,
          status: 'pending',
          dispatched: true,
          lastUpdatedAt: current?.lastUpdatedAt,
        },
      };
    }
    case 'resolved':
      return {
        ...state,
        [action.name]: {
          data: action.data,
          status: 'resolved',
          lastUpdatedAt: action.at,
        },
      };
    case 'empty':
      return {
        ...state,
        [action.name]: {
          data: action.data,
          status: 'empty',
          lastUpdatedAt: action.at,
        },
      };
    case 'error':
      return {
        ...state,
        [action.name]: {
          data: undefined,
          status: 'error',
          error: action.error,
          lastUpdatedAt: state[action.name]?.lastUpdatedAt,
        },
      };
    default:
      return state;
  }
}

type FormStateAction =
  | {
      readonly type: 'set';
      readonly key: string;
      readonly value: FormStateValue;
    }
  | {
      readonly type: 'replace';
      readonly next: FormState;
    };

function formStateReducer(
  state: FormState,
  action: FormStateAction,
): FormState {
  switch (action.type) {
    case 'set':
      // By value, not by reference: a multi-value control re-emitting the same
      // selection hands over a new array each time, and treating that as a
      // change commits state and re-runs every source keyed on this entry.
      if (formStateValuesEqual(state[action.key], action.value)) {
        return state;
      }
      return { ...state, [action.key]: action.value };
    case 'replace':
      return action.next;
    default:
      return state;
  }
}

/**
 * DataSourceManagerProvider.
 *
 * Owns the full fetch lifecycle for `dataSources` declared at
 * `root.props.dataSources`. Runs sources in dependency order on mount,
 * re-runs affected sources when `formState` changes, and exposes resolved
 * data + a generic `formState`/`updateFormState` API via React context.
 *
 * **Generic by design**: no DE / flowState / lead-capture knowledge.
 * `ConfigurableStep` translates `onFormStateChange` into DE state writes.
 *
 * **Cache locality**: `useRef` per provider instance so flow transitions
 * that unmount and remount the provider start with a clean slate.
 *
 * **Abort safety**: per-source `AbortController`s are tracked in a ref
 * and cancelled on unmount / re-run. Never throws; every source
 * degrades independently.
 */
export const DataSourceManagerProvider: React.FC<DataSourceManagerProps> = ({
  dataSources,
  flowContext,
  onFormStateChange,
  initialFormState,
  endpointExcludedFormKeys,
  trustedOrigins,
  tenantId,
  coerceRequestBody,
  coerceResponse,
  reportingQueryUrl,
  inferenceUrl,
  topologySource,
  maxConcurrentByKind,
  serverResolvedSources,
  children,
}) => {
  const nodeSourcesRef = useRef<
    Record<
      string,
      {
        declaration: ReportingModelDataSourceDeclaration;
        scope: Readonly<Record<string, unknown>>;
        refs: number;
      }
    >
  >({});
  const [nodeSourceEpoch, setNodeSourceEpoch] = useState(0);
  const mergedDataSources = useMemo(() => {
    const ids = Object.keys(nodeSourcesRef.current);
    if (ids.length === 0) return dataSources;
    const merged: Record<string, DataSourceDeclaration> = { ...dataSources };
    for (const id of ids) {
      if (!Object.prototype.hasOwnProperty.call(dataSources, id)) {
        merged[id] = nodeSourcesRef.current[id].declaration;
      }
    }
    return merged;
  }, [dataSources, nodeSourceEpoch]);
  // Keyed on declaration *content*, not map identity. Hosts commonly rebuild
  // this map by spread (`{...declared, ...registered}`) on renders that changed
  // no declaration, and the mount effect below resets every source to `pending`
  // and re-runs the whole graph whenever the sort's output changes identity.
  // A new-but-equal map must therefore not reach that effect: it would discard
  // resolved data, and `trigger: 'explicit'` sources are deliberately skipped
  // by the re-run, so nothing would bring them back.
  const dataSourcesSignature = useMemo(
    () => stableStringify(mergedDataSources),
    [mergedDataSources],
  );
  const { ordered: orderedNames, missingDependencies } = useMemo(
    () => topologicalSort(mergedDataSources),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataSourcesSignature],
  );

  const missingBySource = useMemo(() => {
    const map = new Map<string, string>();
    for (const { source, missing } of missingDependencies) {
      if (!map.has(source)) {
        map.set(source, missing);
      }
    }
    return map;
  }, [missingDependencies]);

  // Read once. Each entry is offered to its source's first run only, which
  // uses it when that run would send the request the server sent.
  const seedFromHost = useServerResolvedSources();
  const serverSeedRef = useRef<Map<string, ServerResolvedSource> | null>(null);
  if (serverSeedRef.current === null) {
    serverSeedRef.current = readServerResolvedSources(
      serverResolvedSources ?? seedFromHost,
    );
  }
  // Entries a first run used because it matched the server's request. An
  // effect replay (StrictMode) offers them again, so a remount on the same
  // instance does not refetch what the server resolved. A run that fetches
  // drops its entry, so a refetch or changed request is never answered here.
  const confirmedSeedRef = useRef<Map<string, ServerResolvedSource>>(new Map());

  const [sources, dispatchSources] = useReducer(
    sourcesReducer,
    { orderedNames, missingBySource, seed: serverSeedRef.current },
    ({ orderedNames: names, missingBySource: missing, seed }) => {
      const initial: Record<string, ResolvedDataSource> = {};
      for (const name of names) {
        const missingDep = missing.get(name);
        const seeded = seed.get(name);
        if (missingDep !== undefined) {
          initial[name] = {
            data: undefined,
            status: 'error',
            error: new DataSourceMissingDependencyError(name, missingDep),
          };
        } else if (seeded) {
          initial[name] = {
            data: seeded.data,
            status: seeded.status,
            lastUpdatedAt: seeded.resolvedAt,
            provisional: true,
          };
        } else {
          initial[name] = { data: undefined, status: 'pending' };
        }
      }
      return initial;
    },
  );

  const sourcesRef = useRef(sources);
  sourcesRef.current = sources;

  const [formState, dispatchFormState] = useReducer(
    formStateReducer,
    initialFormState ?? {},
    (seed) => ({ ...seed }),
  );

  const cacheRef = useRef<DataSourceCache | null>(null);
  if (!cacheRef.current) {
    cacheRef.current = createDataSourceCache();
  }
  const abortRef = useRef<Map<string, AbortController>>(new Map());
  const formStateRef = useRef<FormState>(formState);
  const endpointExcludedFormKeysRef = useRef(endpointExcludedFormKeys);
  endpointExcludedFormKeysRef.current = endpointExcludedFormKeys;
  const flowContextRef = useRef<Record<string, unknown>>(flowContext);
  const mountedRef = useRef<boolean>(true);
  const onFormStateChangeRef =
    useRef<typeof onFormStateChange>(onFormStateChange);
  const declarationsRef =
    useRef<Record<string, DataSourceDeclaration>>(dataSources);
  const trustedOriginsRef = useRef<readonly string[] | undefined>(
    trustedOrigins,
  );
  const tenantIdRef = useRef<string | undefined>(tenantId);
  const coerceRequestBodyRef =
    useRef<typeof coerceRequestBody>(coerceRequestBody);
  const coerceResponseRef = useRef<typeof coerceResponse>(coerceResponse);
  // One bag rather than a ref per host-supplied endpoint. Source types read
  // what they need out of it, so adding a type that needs its own endpoint
  // does not add another parameter to every function between here and a
  // fetcher.
  const hostConfigRef = useRef<DataSourceHostConfig>({
    reportingQueryUrl,
    inferenceUrl,
  });
  const topologySourceRef = useRef(topologySource);
  const injectedRoute = useRoute();
  const routeRef = useRef<CeRoute | undefined>(injectedRoute);
  const publishedScope = useOptionalPublishedScope();
  const scopeRef = useRef(publishedScope);
  const missingBySourceRef = useRef(missingBySource);
  const pendingFormCallbackRef = useRef<FormState | null>(null);
  const lastCacheKeyBySourceRef = useRef<Map<string, string>>(new Map());
  // Non-forced TTL fetches in flight, keyed by request identity. Registered
  // before the await so a sibling that derives the same key waits instead of
  // missing the cache. An aborted or failed leader writes nothing and
  // releases the waiters, who then fetch themselves.
  const inflightByCacheKeyRef = useRef<Map<string, Promise<void>>>(new Map());
  // Non-forced calls share one in-flight promise when they would send the
  // same request and that attempt's controller is still the live one.
  // A canceled or replaced attempt is not joined. `force` always starts
  // another attempt.
  const inflightBySourceRef = useRef<
    Map<
      string,
      {
        readonly promise: Promise<void>;
        readonly cacheKey: string;
        readonly controller: AbortController;
        readonly authorizationKey: string;
      }
    >
  >(new Map());
  // Dispatched inference attempts, keyed by source name to the attempt that
  // sent the request. Unmount does not abort that attempt. An older attempt
  // finishing must not clear a newer attempt's marker.
  const dispatchedInferenceRef = useRef<
    Map<string, { attempt: number; controller: AbortController }>
  >(new Map());
  const inferenceAttemptSeqRef = useRef(0);
  const maxConcurrentByKindRef = useRef(maxConcurrentByKind);
  const kindGateRef = useRef<ReturnType<typeof createKindGate> | null>(null);
  if (!kindGateRef.current) {
    kindGateRef.current = createKindGate(() => maxConcurrentByKindRef.current);
  }
  // Reconcile bookkeeping: whether the graph has been seeded once, and the
  // per-name declaration content signature the running graph reflects. The
  // teardown effect resets both on unmount so a remount re-seeds from scratch.
  const didSeedRef = useRef<boolean>(false);
  // The last `fetchWhen` result each gated source ran with, so a host
  // `flowContext` change re-runs only the sources whose gate flipped.
  const gateOpenRef = useRef<Map<string, boolean>>(new Map());
  const declSignaturesRef = useRef<Map<string, string>>(new Map());

  formStateRef.current = formState;
  flowContextRef.current = flowContext;
  onFormStateChangeRef.current = onFormStateChange;
  declarationsRef.current = mergedDataSources;
  trustedOriginsRef.current = trustedOrigins;
  tenantIdRef.current = tenantId;
  coerceRequestBodyRef.current = coerceRequestBody;
  coerceResponseRef.current = coerceResponse;
  hostConfigRef.current = { reportingQueryUrl, inferenceUrl };
  topologySourceRef.current = topologySource;
  routeRef.current = injectedRoute;
  scopeRef.current = publishedScope;
  maxConcurrentByKindRef.current = maxConcurrentByKind;
  missingBySourceRef.current = missingBySource;

  // Raising the cap only updates the ref above. Queued calls stay queued
  // until something asks the gate to read that number again.
  useEffect(() => {
    kindGateRef.current?.refreshLimits();
  }, [maxConcurrentByKind]);

  /**
   * Hot snapshot of the latest resolved data per source — seeded from
   * `sourcesRef` but updated synchronously inside {@link runSource} so
   * that a follow-up `runSource(b)` in the same loop sees the data
   * produced by the just-completed `runSource(a)` without waiting for
   * React to flush the reducer.
   */
  const liveDataRef = useRef<Record<string, unknown>>({});

  const runSource = useCallback(
    (
      name: string,
      opts?: { readonly force?: boolean; readonly body?: unknown },
    ): Promise<void> => {
      // Missing-dependency sources are seeded as `status: 'error'` on reset —
      // do not fetch or flip them back to pending.
      if (missingBySourceRef.current.has(name)) {
        return Promise.resolve();
      }

      const declared = declarationsRef.current[name];
      if (!declared) {
        return Promise.resolve();
      }

      // Caller-supplied body wins for this run only. Applied before cache-key
      // derivation so an overridden payload cannot read a result cached for a
      // different one.
      const hasBodyOverride = opts != null && 'body' in opts;
      const decl = hasBodyOverride
        ? ({ ...declared, body: opts.body } as DataSourceDeclaration)
        : declared;

      // Abort and forget a run this call is replacing. Deleting the join
      // entry here matters: the aborted promise stays pending until its
      // fetch settles, and a later call must not join that doomed promise.
      const dropSupersededSourceRun = (): void => {
        const existing = abortRef.current.get(name);
        if (existing !== undefined && !existing.signal.aborted) {
          existing.abort(
            new Error('DataSource re-run: superseded by newer run.'),
          );
        }
        inflightBySourceRef.current.delete(name);
      };

      // A precondition that is not allowed, such as one that reads a
      // decision outcome, refuses the source outright rather than gating it.
      const refusal = fetchWhenRefusal(decl.fetchWhen);
      if (refusal !== null) {
        dropSupersededSourceRun();
        delete liveDataRef.current[name];
        gateOpenRef.current.delete(name);
        warnOnce(
          'refused-fetch-when',
          `[DataSourceManager] Source "${name}" is not fetched: ${refusal}`,
        );
        if (mountedRef.current) {
          dispatchSources({
            type: 'error',
            name,
            error: new DataSourcePreconditionRefusedError(name, refusal),
          });
        }
        return Promise.resolve();
      }

      // Precondition gate: a source no precondition currently needs is never
      // requested, forced or not. It reads raw inputs only, so it decides
      // before any interpolation or dependency check, and before the
      // explicit-trigger deferral below, so a gate that closes also clears a
      // source that only an imperative refetch would run.
      const gateOpen = fetchWhenSatisfied(decl.fetchWhen, {
        context: flowContextRef.current,
        formState: formStateRef.current,
      });
      if (decl.fetchWhen && decl.fetchWhen.length > 0) {
        gateOpenRef.current.set(name, gateOpen);
      }
      if (!gateOpen) {
        dropSupersededSourceRun();
        delete liveDataRef.current[name];
        if (mountedRef.current) {
          dispatchSources({ type: 'skipped', name });
        }
        return Promise.resolve();
      }

      // Cost-safe gate: a source type may hold a declaration pending until an
      // imperative refetch (`force: true`) — today, inference sources set to
      // `trigger: 'explicit'`. Mount / formState paths omit force and must not
      // fetch. A source the precondition gate had skipped goes back to plain
      // `pending`, waiting for that refetch.
      if (
        !opts?.force &&
        resolveDataSourceType(decl)?.deferUntilForced?.(decl)
      ) {
        if (mountedRef.current && sourcesRef.current[name]?.skipped) {
          dispatchSources({ type: 'pending', name });
        }
        return Promise.resolve();
      }

      const topology: TopologyProjection = topologySourceRef.current
        ? projectCanvasTopology(topologySourceRef.current)
        : { siblings: {} };
      const nodeEntry = nodeSourcesRef.current[name];
      const scopeForRun = nodeEntry ? nodeEntry.scope : scopeRef.current;
      const interpolationContext = buildContextFromLiveData(
        flowContextRef.current,
        formStateRef.current,
        liveDataRef.current,
        routeRef.current,
        scopeForRun,
        topology,
        endpointExcludedFormKeysRef.current,
      );
      const derivedRoots = resolveDataSourceType(decl)?.derivedRoots?.(
        decl,
        interpolationContext,
      );
      if (derivedRoots) {
        const shadowed = assignDerivedRoots(
          interpolationContext as Record<string, unknown>,
          derivedRoots,
        );
        for (let i = 0; i < shadowed.length; i++) {
          warnOnce(
            'derived-root-shadow',
            `[DataSourceManager] Source "${name}" reads "{{${shadowed[i]}.*}}" from its own declaration; the data source named "${shadowed[i]}" is not visible to it.`,
          );
        }
      }

      const absentBlockId = findAbsentTopologyBlockId(decl, topology);
      if (absentBlockId !== undefined) {
        dropSupersededSourceRun();
        delete liveDataRef.current[name];
        if (mountedRef.current) {
          dispatchSources({
            type: 'error',
            name,
            error: new TopologyBlockAbsentError(absentBlockId),
          });
        }
        return Promise.resolve();
      }

      // Conditional-execution guard: a source templated on a dependency that
      // is not yet present — e.g. `{{formState.selectedOfferingId}}` in the
      // endpoint before any result is selected — must stay `pending` rather
      // than fire `GET …/v1/offerings/`. Empty `formState.*` in query is
      // omitted at interpolate time instead of deferring, so optional
      // facets can start unset. Sibling-source tokens still defer. The
      // `affectedSourcesByFormStateKey` re-run path fires the source the
      // moment a gating dependency is written. Static endpoints are never
      // skipped.
      if (
        hasUnresolvedDependency(decl, interpolationContext) ||
        reportingSourceMustWait(decl, interpolationContext)
      ) {
        // Drop any previously-resolved snapshot for this source. If a dependency
        // is cleared after the source had resolved (e.g. the selected offering is
        // deselected), leaving stale data in `liveDataRef` would let dependents
        // resolve `{{<name>.*}}` tokens against it and issue incorrect requests.
        // A reporting declaration that names a scope key nobody published stays
        // pending for the same reason: the request must not carry the sentinel.
        dropSupersededSourceRun();
        delete liveDataRef.current[name];
        if (mountedRef.current) {
          dispatchSources({ type: 'pending', name });
        }
        return Promise.resolve();
      }

      if (!readyWhenSatisfied(decl.readyWhen, interpolationContext)) {
        dropSupersededSourceRun();
        delete liveDataRef.current[name];
        if (mountedRef.current) {
          dispatchSources({ type: 'pending', name });
        }
        return Promise.resolve();
      }

      const trafficSource = readTrafficSource(flowContextRef.current);
      const presentationUrl = readPresentationUrl();
      const flowType = readFlowType(flowContextRef.current);
      // Captured with the cache key. The fetch below can wait on an in-flight
      // sibling, and a host URL that changes during that wait must not store
      // the new response under this key.
      const hostConfig = hostConfigRef.current;
      const coerceRequest = (req: InterpolatedRequest): InterpolatedRequest => {
        const coerced = coerceRequestBodyRef.current?.(req) ?? req;
        const withFlowType = injectFlowTypeIntoOfferingsRequest(
          coerced,
          flowType,
        );
        const withTrafficSource = injectTrafficSourceIntoOfferingsRequest(
          withFlowType,
          trafficSource,
        );
        return injectPresentationUrlIntoOfferingsRequest(
          withTrafficSource,
          presentationUrl,
        );
      };

      // Build and coerce once. The cache key and the fetch must share this
      // object: a coercer that mints a nonce or timestamp would otherwise key
      // one request and send another.
      const sourceType = resolveDataSourceType(decl);
      const preparedRequest = sourceType
        ? coerceRequest(
            sourceType.buildRequest(decl, interpolationContext, hostConfig),
          )
        : undefined;
      const requestIdentity = preparedRequest
        ? deriveCacheKey(
            preparedRequest.method,
            preparedRequest.endpoint,
            preparedRequest.query,
            preparedRequest.body,
            preparedRequest.headers,
            decl.responsePath,
          )
        : deriveCacheKeyForDecl(decl, interpolationContext, hostConfig);

      // The server resolved this source for the page. Its first run uses that
      // data instead of fetching when it would send the same request; a run
      // that differs, a forced run, or a body override fetches as before.
      const seeded = serverSeedRef.current?.get(name);
      if (seeded) {
        serverSeedRef.current?.delete(name);
        if (
          !opts?.force &&
          !hasBodyOverride &&
          seeded.request === identityFingerprint(requestIdentity)
        ) {
          confirmedSeedRef.current.set(name, seeded);
          dropSupersededSourceRun();
          liveDataRef.current[name] = seeded.data;
          if (mountedRef.current) {
            dispatchSources({
              type: seeded.status,
              name,
              data: seeded.data,
              at: seeded.resolvedAt,
            });
          }
          return Promise.resolve();
        }
      }
      confirmedSeedRef.current.delete(name);

      const cacheKey = appendNodeScopeCacheSuffix(
        appendPresentationUrlCacheSuffix(
          appendTrafficSourceCacheSuffix(
            appendFlowTypeCacheSuffix(
              requestIdentity,
              flowContextRef.current.flowType,
            ),
            trafficSource,
          ),
          presentationUrl,
        ),
        nodeEntry?.scope,
      );
      const cache = cacheRef.current;

      // Track the key only for TTL-cached sources. Keys are derived from
      // request identity, not source name, so tracking an uncached source
      // would let `invalidate` evict a colliding cached source's entry.
      if (decl.cache?.ttlMs) {
        lastCacheKeyBySourceRef.current.set(name, cacheKey);
      } else {
        lastCacheKeyBySourceRef.current.delete(name);
      }

      // Registered only once this run will fetch or wait on a cache sibling.
      // An earlier return (absent block, unresolved dependency, readiness)
      // must not be the promise a later non-force call joins.
      // Same request identity joins. A changed form value does not.
      if (!opts?.force) {
        const inflight = inflightBySourceRef.current.get(name);
        const liveController = abortRef.current.get(name);
        if (
          inflight !== undefined &&
          inflight.cacheKey === cacheKey &&
          inflight.authorizationKey ===
            authorizationKey(tenantIdRef.current, trustedOriginsRef.current) &&
          liveController !== undefined &&
          inflight.controller === liveController &&
          !liveController.signal.aborted
        ) {
          return inflight.promise;
        }
      }

      dropSupersededSourceRun();
      const controller = new AbortController();
      abortRef.current.set(name, controller);
      if (mountedRef.current) {
        dispatchSources({ type: 'pending', name });
      }

      const currentDeclFor = (): DataSourceDeclaration | undefined => {
        const currentDeclared = declarationsRef.current[name];
        if (!currentDeclared) return undefined;
        return hasBodyOverride
          ? ({ ...currentDeclared, body: opts?.body } as DataSourceDeclaration)
          : currentDeclared;
      };
      // Captured before the permit wait. A queued call must not send the
      // request it built if the tenant, the page address, the dependency,
      // the declaration, or the canvas changed while it was waiting.
      const sourceRunFingerprint = (): string => {
        const currentDecl = currentDeclFor();
        const depNames = currentDecl
          ? executionDependencyNames(currentDecl)
          : [];
        const depData: Record<string, unknown> = {};
        for (const dep of depNames) {
          depData[dep] = liveDataRef.current[dep];
        }
        const topology = topologySourceRef.current
          ? projectCanvasTopology(topologySourceRef.current)
          : { siblings: {} };
        return stableStringify({
          decl: currentDecl ?? null,
          form: formStateRef.current,
          flow: flowContextRef.current,
          route: routeRef.current ?? null,
          scope: scopeForRun ?? null,
          depData,
          topology,
          host: hostConfigRef.current,
          tenant: tenantIdRef.current ?? null,
          origins: trustedOriginsRef.current ?? null,
          presentationUrl: readPresentationUrl() ?? null,
          trafficSource: readTrafficSource(flowContextRef.current) ?? null,
        });
      };
      const capturedFingerprint = sourceRunFingerprint();
      const capturedAuthorization = authorizationKey(
        tenantIdRef.current,
        trustedOriginsRef.current,
      );

      const runHolder: { current?: Promise<void> } = {};
      const run = (async (): Promise<void> => {
        // Force refetch must not serve a warm TTL entry (hard refresh / Retry).
        // Everyone else shares one in-flight fetch per cache key: a parallel
        // sibling used to miss the entry the serial walk would have written.
        let releaseInflight: (() => void) | undefined;
        let retry = false;
        if (!opts?.force && cache && decl.cache?.ttlMs) {
          const publishCached = (): boolean => {
            // The entry rather than the value, so the source reports when the
            // data was fetched instead of when this hit read it -- otherwise
            // every cache hit inside the TTL window looks freshly loaded.
            const entry = cache.getEntry(cacheKey);
            const cached = entry?.data as unknown;
            if (entry === undefined || cached === undefined) {
              return false;
            }
            liveDataRef.current[name] = cached;
            if (!mountedRef.current) {
              return true;
            }
            if (isEmpty(cached)) {
              dispatchSources({
                type: 'empty',
                name,
                data: cached,
                at: entry.storedAt,
              });
            } else {
              dispatchSources({
                type: 'resolved',
                name,
                data: cached,
                at: entry.storedAt,
              });
            }
            return true;
          };

          for (;;) {
            if (sourceRunFingerprint() !== capturedFingerprint) {
              retry = true;
              break;
            }
            if (publishCached()) {
              return;
            }
            const inflight = inflightByCacheKeyRef.current.get(cacheKey);
            if (inflight === undefined) {
              let release!: () => void;
              const pending = new Promise<void>((resolve) => {
                release = resolve;
              });
              inflightByCacheKeyRef.current.set(cacheKey, pending);
              releaseInflight = () => {
                if (inflightByCacheKeyRef.current.get(cacheKey) === pending) {
                  inflightByCacheKeyRef.current.delete(cacheKey);
                }
                release();
              };
              break;
            }
            await inflight;
            if (!mountedRef.current || controller.signal.aborted) {
              return;
            }
          }
        }

        if (!retry) {
          let result: Awaited<ReturnType<typeof dispatchFetchDataSource>>;
          const kind = getDataSourceKind(decl);
          const permit = await kindGateRef.current!.acquire(
            kind,
            controller.signal,
          );
          let dispatchedInference = false;
          let inferenceAttempt = 0;
          try {
            if (!mountedRef.current || controller.signal.aborted) {
              return;
            }
            if (sourceRunFingerprint() !== capturedFingerprint) {
              // Run the normal checks again, and wait for that run. A removed
              // block reports its error here. A dependent stays behind this
              // promise until the replacement settles.
              retry = true;
            }
            if (!retry) {
              if (kind === 'inference') {
                inferenceAttempt = inferenceAttemptSeqRef.current + 1;
                inferenceAttemptSeqRef.current = inferenceAttempt;
                dispatchedInferenceRef.current.set(name, {
                  attempt: inferenceAttempt,
                  controller,
                });
                dispatchedInference = true;
              }
              if (mountedRef.current) {
                dispatchSources({ type: 'dispatched', name });
              }
              result = await dispatchFetchDataSource(
                decl,
                interpolationContext,
                {
                  signal: controller.signal,
                  trustedOrigins: trustedOriginsRef.current,
                  tenantId: tenantIdRef.current,
                  ...hostConfig,
                  preparedRequest,
                  coerceResponse: coerceResponseRef.current,
                },
              );

              if (!mountedRef.current || controller.signal.aborted) {
                return;
              }
              // A newer attempt may already own this source. StrictMode leaves
              // the first request running and starts a second one, so the
              // first response must not replace the second.
              if (
                dispatchedInference &&
                dispatchedInferenceRef.current.get(name)?.attempt !==
                  inferenceAttempt
              ) {
                return;
              }

              if (result.status === 'resolved' || result.status === 'empty') {
                if (cache && decl.cache?.ttlMs) {
                  cache.set(cacheKey, result.data, decl.cache.ttlMs);
                }
                liveDataRef.current[name] = result.data;
              } else {
                liveDataRef.current[name] = undefined;
              }

              const fetchedAt = Date.now();
              switch (result.status) {
                case 'resolved':
                  dispatchSources({
                    type: 'resolved',
                    name,
                    data: result.data,
                    at: fetchedAt,
                  });
                  break;
                case 'empty':
                  dispatchSources({
                    type: 'empty',
                    name,
                    data: result.data,
                    at: fetchedAt,
                  });
                  break;
                case 'error':
                  dispatchSources({
                    type: 'error',
                    name,
                    error:
                      result.error ??
                      new Error(`DataSource "${name}" failed without a cause.`),
                  });
                  break;
                case 'pending':
                default:
                  break;
              }
            }
          } finally {
            if (
              dispatchedInference &&
              dispatchedInferenceRef.current.get(name)?.attempt ===
                inferenceAttempt
            ) {
              dispatchedInferenceRef.current.delete(name);
            }
            permit.release();
            releaseInflight?.();
          }
        }
        if (retry) {
          if (
            inflightBySourceRef.current.get(name)?.promise === runHolder.current
          ) {
            inflightBySourceRef.current.delete(name);
          }
          await runSource(name, opts);
        }
      })();
      runHolder.current = run;

      inflightBySourceRef.current.set(name, {
        promise: run,
        cacheKey,
        controller,
        authorizationKey: capturedAuthorization,
      });
      const dropInflight = (): void => {
        if (inflightBySourceRef.current.get(name)?.promise === run) {
          inflightBySourceRef.current.delete(name);
        }
      };
      // A rejected `run` must not produce a second rejection here. The caller
      // handles `run`; this side promise only removes the join entry.
      void run.then(dropInflight, dropInflight);
      return run;
    },
    [],
  );

  /**
   * Walk `names` one dependency level at a time. Members of a level share a
   * cursor and run together up to `concurrency` (default
   * {@link DEFAULT_REFETCH_CONCURRENCY}). The level is awaited before the next
   * so `runSource` has written `liveDataRef` before a dependent interpolates.
   * `force` is forwarded only when the caller set it — mount, reconcile, and
   * form-state omit it so the TTL cache stays and `trigger: 'explicit'`
   * inference sources stay pending.
   */
  const runDependencyLevels = useCallback(
    async (
      names: readonly string[],
      opts?: {
        readonly force?: boolean;
        readonly concurrency?: number;
        readonly generation?: number;
        /** Attached only when the worker's name matches. Other names omit `body`. */
        readonly bodyFor?: { readonly name: string; readonly body: unknown };
      },
    ): Promise<void> => {
      const levels = dependencyLevels(
        declarationsRef.current,
        orderedNames,
        names,
      );
      const requested = opts?.concurrency ?? DEFAULT_REFETCH_CONCURRENCY;
      const limit = Number.isFinite(requested)
        ? Math.max(1, Math.trunc(requested))
        : DEFAULT_REFETCH_CONCURRENCY;
      const generation = opts?.generation ?? providerRerunGenerationRef.current;
      const superseded = (): boolean =>
        generation !== providerRerunGenerationRef.current;
      const walk: InflightWalk = {
        generation,
        names: new Set(names),
      };
      inflightWalksRef.current.push(walk);

      try {
        for (const level of levels) {
          if (!mountedRef.current || superseded()) {
            return;
          }
          // Workers share a cursor. Nothing awaits between the read and the
          // increment, so two workers cannot take the same name.
          let cursor = 0;
          const workers = Array.from(
            { length: Math.min(limit, level.length) },
            async () => {
              while (cursor < level.length) {
                const target = level[cursor];
                cursor += 1;
                if (
                  target === undefined ||
                  !mountedRef.current ||
                  superseded()
                ) {
                  return;
                }
                await runSource(target, {
                  ...(opts?.force !== undefined ? { force: opts.force } : {}),
                  ...(opts?.bodyFor?.name === target
                    ? { body: opts.bodyFor.body }
                    : {}),
                });
                walk.names.delete(target);
              }
            },
          );
          await Promise.all(workers);
        }
      } finally {
        const walks = inflightWalksRef.current;
        const index = walks.indexOf(walk);
        if (index >= 0) {
          walks.splice(index, 1);
        }
      }
    },
    [orderedNames, runSource],
  );

  const runSources = useCallback(
    async (
      names: readonly string[],
      opts?: { readonly force?: boolean; readonly generation?: number },
    ): Promise<void> => {
      await runDependencyLevels(names, opts);
    },
    [runDependencyLevels],
  );

  const providerRerunGenerationRef = useRef(0);
  const inflightWalksRef = useRef<InflightWalk[]>([]);
  const routeContentKey = serializeInjectedRoute(injectedRoute);
  const scopeContentKey =
    publishedScope === undefined ? '' : scopeContentSignature(publishedScope);
  const seenRouteKeyRef = useRef<string | null>(null);
  const seenScopeKeyRef = useRef<string | null>(null);

  // Reconcile the running graph against the latest declarations. Because the
  // sort — and therefore this effect — is keyed on the content signature, an
  // identity-only map rebuild never reaches here. When it does run:
  //   - first seed (mount / remount): reset every name and run the graph,
  //     exactly as before;
  //   - a genuine content change: diff previous vs current declarations and
  //     touch only what moved. Unchanged names that do not wait on a
  //     refetched source keep their resolved snapshot (no `pending` flash,
  //     no refetch, no cache eviction, no abort). After that source
  //     settles, names that wait on it run again, including ones that
  //     already resolved. Added / content-changed names invalidate *only
  //     their own* cache key and refetch; removed names are aborted,
  //     evicted, and dropped.
  useEffect(() => {
    mountedRef.current = true;
    const controllers = abortRef.current;
    const decls = declarationsRef.current;

    // Per-name content signature the graph should reflect after this run. A
    // missing dependency folds into the signature so a name toggling in/out of
    // the missing state is treated as a change (re-seed error / refetch).
    const nextSignatures = new Map<string, string>();
    for (const name of orderedNames) {
      const missing = missingBySource.get(name);
      nextSignatures.set(
        name,
        missing !== undefined
          ? `\u0000missing:${missing}`
          : stableStringify(decls[name]),
      );
    }

    const previousRoute = seenRouteKeyRef.current;
    const previousScope = seenScopeKeyRef.current;
    seenRouteKeyRef.current = routeContentKey;
    seenScopeKeyRef.current = scopeContentKey;

    if (!didSeedRef.current) {
      didSeedRef.current = true;
      liveDataRef.current = {};
      lastCacheKeyBySourceRef.current.clear();
      declSignaturesRef.current = nextSignatures;
      const seeded = new Set<string>();
      serverSeedRef.current?.forEach((_entry, name) => {
        seeded.add(name);
      });
      dispatchSources({
        type: 'reset',
        names: orderedNames,
        missingBySource,
        seeded,
      });
      void runSources(orderedNames);
      return;
    }

    const prevSignatures = declSignaturesRef.current;
    const declaredNames = new Set(orderedNames);
    const refetch = new Set<string>();

    const dropSource = (name: string, reason: string): void => {
      const controller = controllers.get(name);
      if (controller) {
        controller.abort(new Error(reason));
        controllers.delete(name);
      }
      inflightBySourceRef.current.delete(name);
      const key = lastCacheKeyBySourceRef.current.get(name);
      if (key !== undefined) {
        cacheRef.current?.delete(key);
        lastCacheKeyBySourceRef.current.delete(name);
      }
      delete liveDataRef.current[name];
    };

    // Removed: names present last run but no longer declared.
    let removedAny = false;
    prevSignatures.forEach((_signature, name) => {
      if (!declaredNames.has(name)) {
        dropSource(name, 'DataSource removed from declarations.');
        removedAny = true;
      }
    });

    // Added or content-changed: everything whose per-name signature moved.
    // Invalidate only that name's cache key, abort only its stale run, and
    // mark it to refetch — unless it is now a missing dependency, which the
    // reducer seeds as `error` and which must never fetch.
    let signatureChanged = false;
    for (const name of orderedNames) {
      if (prevSignatures.get(name) === nextSignatures.get(name)) {
        continue;
      }
      signatureChanged = true;
      dropSource(name, 'DataSource declaration changed.');
      if (missingBySource.get(name) === undefined) {
        refetch.add(name);
      }
    }

    declSignaturesRef.current = nextSignatures;

    const routeChanged =
      previousRoute !== null && previousRoute !== routeContentKey;
    const scopeChanged =
      previousScope !== null && previousScope !== scopeContentKey;
    if (routeChanged) {
      for (const name of affectedSourcesByRoute(decls, orderedNames)) {
        refetch.add(name);
      }
    }
    if (scopeChanged) {
      for (const name of affectedSourcesByTemplateNeedle(
        decls,
        orderedNames,
        'scope',
      )) {
        // A node source reads the scope it registered with, not this one.
        if (nodeSourcesRef.current[name]) continue;
        refetch.add(name);
      }
    }

    if (!removedAny && !signatureChanged && refetch.size === 0) {
      return;
    }

    dispatchSources({
      type: 'reconcile',
      names: orderedNames,
      refetch,
      missingBySource,
    });

    if (refetch.size > 0) {
      const carried = new Set<string>();
      refetch.forEach((name) => {
        carried.add(name);
      });
      let generation = providerRerunGenerationRef.current;
      if (routeChanged || scopeChanged) {
        inflightWalksRef.current.forEach((walk) => {
          walk.names.forEach((walkName) => {
            carried.add(walkName);
          });
        });
        carried.forEach((name) => {
          const controller = abortRef.current.get(name);
          if (controller === undefined || controller.signal.aborted) {
            return;
          }
          controller.abort(
            new Error('DataSource re-run: superseded by newer run.'),
          );
          inflightBySourceRef.current.delete(name);
        });
        generation = ++providerRerunGenerationRef.current;
      }
      const upstreamNames = orderedNames.filter((name) => carried.has(name));
      void (async () => {
        await runSources(upstreamNames, { generation });
        const affected = new Set<string>(upstreamNames);
        const waiters: string[] = [];
        orderedNames.forEach((name) => {
          if (affected.has(name)) return;
          const current = declarationsRef.current[name];
          if (current === undefined) return;
          if (missingBySourceRef.current.has(name)) return;
          if (!waitsOn(current, affected)) return;
          waiters.push(name);
          affected.add(name);
        });
        if (waiters.length > 0) {
          await runSources(waiters, { generation });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderedNames,
    missingBySource,
    routeContentKey,
    scopeContentKey,
    runSources,
  ]);

  // Teardown owns the provider lifecycle so the reconcile effect above never
  // wipes state on a mere content change. On real unmount (and StrictMode's
  // dev double-mount) this aborts every in-flight run, clears the cache, and
  // resets the seed bookkeeping so a remount re-seeds the whole graph.
  useEffect(() => {
    const controllers = abortRef.current;
    return () => {
      mountedRef.current = false;
      controllers.forEach((controller, name) => {
        const dispatched = dispatchedInferenceRef.current.get(name);
        if (dispatched !== undefined && dispatched.controller === controller) {
          return;
        }
        controller.abort(new Error('DataSourceManager unmounted.'));
      });
      controllers.clear();
      inflightBySourceRef.current.clear();
      cacheRef.current?.clear();
      lastCacheKeyBySourceRef.current.clear();
      liveDataRef.current = {};
      declSignaturesRef.current = new Map();
      gateOpenRef.current = new Map();
      didSeedRef.current = false;
      // Ends walks started before teardown. A replay sets `mountedRef` again,
      // so without this an old walk would keep consuming seeds.
      providerRerunGenerationRef.current += 1;
      confirmedSeedRef.current.forEach((entry, name) => {
        serverSeedRef.current?.set(name, entry);
      });
      confirmedSeedRef.current.clear();
    };
  }, []);

  // A host `flowContext` change can open or close a `fetchWhen` gate that
  // reads `context`. Declarations, route, and scope are unchanged, so the
  // reconcile effect never sees it: re-run the sources whose gate flipped,
  // and the sources that wait on them, so an opened gate fetches and a
  // closed one drops its data and cancels its run.
  useEffect(() => {
    const decls = declarationsRef.current;
    const flipped = orderedNames.filter((name) => {
      const fetchWhen = decls[name]?.fetchWhen;
      if (
        !fetchWhen?.some((precondition) => precondition.source === 'context')
      ) {
        return false;
      }
      const last = gateOpenRef.current.get(name);
      if (last === undefined) return false;
      return (
        last !==
        fetchWhenSatisfied(fetchWhen, {
          context: flowContext,
          formState: formStateRef.current,
        })
      );
    });
    if (flipped.length > 0) {
      void runSources(withExecutionDependents(decls, orderedNames, flipped));
    }
  }, [flowContext, orderedNames, runSources]);

  // Sources whose `fetchWhen` reads one of `keys` and whose gate the new form
  // state flips, plus the sources that wait on them. A key a gate reads but
  // no request uses re-runs nothing while the gate stays where it was, so a
  // keystroke in a field an `isNotNull` gate watches never refetches.
  const gateFlippedByFormState = useCallback(
    (keys: readonly string[], nextFormState: FormState): string[] => {
      const decls = declarationsRef.current;
      const flipped = orderedNames.filter((name) => {
        const fetchWhen = decls[name]?.fetchWhen;
        if (!keys.some((key) => fetchWhenReadsFormKey(fetchWhen, key))) {
          return false;
        }
        const last = gateOpenRef.current.get(name);
        return (
          last === undefined ||
          last !==
            fetchWhenSatisfied(fetchWhen, {
              context: flowContextRef.current,
              formState: nextFormState,
            })
        );
      });
      return flipped.length > 0
        ? withExecutionDependents(decls, orderedNames, flipped)
        : [];
    },
    [orderedNames],
  );

  const updateFormState = useCallback(
    (key: string, value: FormStateValue): void => {
      const previous = formStateRef.current[key];
      dispatchFormState({ type: 'set', key, value });

      if (formStateValuesEqual(previous, value)) {
        return;
      }

      const nextFormState: FormState = {
        ...formStateRef.current,
        [key]: value,
      };
      formStateRef.current = nextFormState;

      pendingFormCallbackRef.current = nextFormState;

      const decls = declarationsRef.current;
      const names = new Set([
        ...affectedSourcesByFormStateKey(decls, orderedNames, key),
        ...gateFlippedByFormState([key], nextFormState),
      ]);
      const affected = orderedNames.filter((name) => names.has(name));
      if (affected.length > 0) {
        void runSources(affected);
      }
    },
    [orderedNames, runSources, gateFlippedByFormState],
  );

  const updateFormStateMany = useCallback(
    (entries: Readonly<Record<string, string>>): void => {
      const previous = formStateRef.current;
      const changed = Object.entries(entries).filter(
        ([key, value]) => previous[key] !== value,
      );
      if (changed.length === 0) return;

      const nextFormState: FormState = {
        ...previous,
        ...Object.fromEntries(changed),
      };
      formStateRef.current = nextFormState;
      dispatchFormState({ type: 'replace', next: nextFormState });
      pendingFormCallbackRef.current = nextFormState;

      const affectedNames = new Set<string>();
      const decls = declarationsRef.current;
      for (const [key] of changed) {
        for (const name of affectedSourcesByFormStateKey(
          decls,
          orderedNames,
          key,
        )) {
          affectedNames.add(name);
        }
      }
      for (const name of gateFlippedByFormState(
        changed.map(([key]) => key),
        nextFormState,
      )) {
        affectedNames.add(name);
      }
      const affected = orderedNames.filter((name) => affectedNames.has(name));
      if (affected.length > 0) {
        void runSources(affected);
      }
    },
    [orderedNames, runSources, gateFlippedByFormState],
  );

  const getFormState = useCallback((): FormState => formStateRef.current, []);

  const refetchSource = useCallback(
    async (
      name: string,
      opts?: { readonly cascade?: boolean; readonly body?: unknown },
    ): Promise<void> => {
      if (
        !Object.prototype.hasOwnProperty.call(declarationsRef.current, name)
      ) {
        return;
      }
      const names =
        opts?.cascade === true
          ? cascadeSourceNames(declarationsRef.current, orderedNames, name)
          : [name];
      if (names.length === 0) {
        return;
      }
      const hasBodyOverride = opts != null && 'body' in opts;
      if (!hasBodyOverride) {
        await runSources(names, { force: true });
        return;
      }
      // The override belongs to the named source; cascaded siblings keep their
      // own declared bodies.
      await runDependencyLevels(names, {
        force: true,
        bodyFor: { name, body: opts.body },
      });
    },
    [orderedNames, runDependencyLevels, runSources],
  );

  const refetchAll = useCallback(async (): Promise<void> => {
    await runSources(orderedNames, { force: true });
  }, [orderedNames, runSources]);

  // Named-subset host API: optional concurrency and opt-in dependency
  // closure. The level walk itself is shared with `runSources` / `refetchAll`.
  // This path always forces, so it skips the TTL cache and fires
  // `trigger: 'explicit'` inference sources. Mount, reconcile, and form-state
  // stay on `runSources` and do not.
  const refetchMany = useCallback(
    async (
      names: readonly string[],
      opts?: {
        readonly concurrency?: number;
        readonly includeDependencies?: boolean;
      },
    ): Promise<void> => {
      // Off by default: a caller naming its own subset has already decided what
      // to touch, and widening it would re-run sources it deliberately left
      // alone. A caller that selected by tag cannot make that decision, so it
      // opts in.
      const targets = opts?.includeDependencies
        ? dependencyClosure(declarationsRef.current, orderedNames, names)
        : names;
      await runDependencyLevels(targets, {
        force: true,
        ...(opts?.concurrency !== undefined
          ? { concurrency: opts.concurrency }
          : {}),
      });
    },
    [orderedNames, runDependencyLevels],
  );

  const invalidate = useCallback((name: string): void => {
    const key = lastCacheKeyBySourceRef.current.get(name);
    if (key === undefined) {
      return;
    }
    cacheRef.current?.delete(key);
    lastCacheKeyBySourceRef.current.delete(name);
  }, []);

  // Tags are matched, never interpreted. Declaration order is preserved so a
  // host can report what a refresh will touch in a stable order.
  const namesByTags = useCallback(
    (tags: readonly string[]): readonly string[] => {
      if (tags.length === 0) {
        return [];
      }
      const wanted = new Set(tags);
      return orderedNames.filter((name) =>
        declarationsRef.current[name]?.tags?.some((tag) => wanted.has(tag)),
      );
    },
    [orderedNames],
  );

  // Selecting by tag means the caller did not enumerate what it is re-running,
  // so it cannot know whether the set it named is closed under `dependsOn`. A
  // source whose dependency sits outside the tag re-runs against that
  // dependency's last value and still reports success, which is the one failure
  // a refresh exists to rule out. `includeDependencies` is how a host says it
  // wants the dependency re-run first; it stays opt-in because whether that is
  // wanted depends on what the dependency is for, and tags are matched here,
  // never interpreted.
  const refetchByTags = useCallback(
    async (
      tags: readonly string[],
      opts?: {
        readonly concurrency?: number;
        readonly includeDependencies?: boolean;
      },
    ): Promise<void> => {
      await refetchMany(namesByTags(tags), opts);
    },
    [namesByTags, refetchMany],
  );

  const invalidateByTags = useCallback(
    (tags: readonly string[]): void => {
      for (const name of namesByTags(tags)) {
        invalidate(name);
      }
    },
    [invalidate, namesByTags],
  );

  useEffect(() => {
    const pending = pendingFormCallbackRef.current;
    if (pending !== null && onFormStateChangeRef.current) {
      pendingFormCallbackRef.current = null;
      onFormStateChangeRef.current(pending);
    }
  }, [formState]);

  const registerNodeSource = useCallback(
    (
      id: string,
      declaration: ReportingModelDataSourceDeclaration,
      scope: Readonly<Record<string, unknown>>,
    ) => {
      const existing = nodeSourcesRef.current[id];
      if (existing) {
        existing.refs += 1;
        return;
      }
      nodeSourcesRef.current[id] = { declaration, scope, refs: 1 };
      setNodeSourceEpoch((epoch) => epoch + 1);
    },
    [],
  );
  const unregisterNodeSource = useCallback((id: string) => {
    const existing = nodeSourcesRef.current[id];
    if (!existing) return;
    existing.refs -= 1;
    if (existing.refs > 0) return;
    delete nodeSourcesRef.current[id];
    setNodeSourceEpoch((epoch) => epoch + 1);
  }, []);

  const contextValue = useMemo<DataSourceManagerContextValue>(
    () => ({
      sources,
      formState,
      getFormState,
      flowContext,
      getSource: (name: string) => sources[name],
      getDeclaration: (name: string) => declarationsRef.current[name],
      updateFormState,
      updateFormStateMany,
      refetchSource,
      refetchAll,
      refetchMany,
      invalidate,
      namesByTags,
      refetchByTags,
      invalidateByTags,
      registerNodeSource,
      unregisterNodeSource,
    }),
    [
      sources,
      formState,
      getFormState,
      flowContext,
      updateFormState,
      updateFormStateMany,
      refetchSource,
      refetchAll,
      refetchMany,
      invalidate,
      namesByTags,
      refetchByTags,
      invalidateByTags,
      registerNodeSource,
      unregisterNodeSource,
    ],
  );

  return (
    <DataSourceManagerContext.Provider value={contextValue}>
      {children}
    </DataSourceManagerContext.Provider>
  );
};

/**
 * Hook that returns the {@link DataSourceManagerContextValue} for the
 * nearest {@link DataSourceManagerProvider}. Throws outside a provider —
 * consumers embedded under a configurable step are expected to always
 * have one in scope.
 */
export function useDataSourceManager(): DataSourceManagerContextValue {
  const context = useContext(DataSourceManagerContext);
  if (context === undefined) {
    throw new Error(
      'useDataSourceManager must be used within a <DataSourceManagerProvider>.',
    );
  }
  return context;
}

/**
 * Non-throwing variant of {@link useDataSourceManager}. Returns
 * `undefined` when no {@link DataSourceManagerProvider} is mounted in
 * the React tree. Used by render surfaces that may be composed under
 * a host bridge (e.g. `ConfigurableStep`) which already owns the DSM,
 * or rendered standalone (legacy step types, Storybook). Lets the
 * inner consumer detect ownership and skip a redundant provider —
 * preventing the inner DSM from shadowing the bridge's `flowContext`
 * and `onFormStateChange` wiring.
 */
export function useOptionalDataSourceManager():
  | DataSourceManagerContextValue
  | undefined {
  return useContext(DataSourceManagerContext);
}

/**
 * Names that are reserved as interpolation roots and must NEVER be
 * shadowed by a user-declared data source. The binding/action
 * resolvers treat `{{context.*}}`, `{{formState.*}}`, and
 * `{{topology.*}}` as built-in roots — if a source with one of those
 * names were allowed to overwrite them, every existing template
 * referencing those roots would silently start reading from the
 * data-source response instead. Validation is enforced at
 * `buildContextFromLiveData` (the only entry point that produces
 * the merged interpolation context) so the rule applies to every
 * fetch, every cache-key derivation, and every dependency rerun.
 */
const RESERVED_INTERPOLATION_ROOTS: ReadonlySet<string> = new Set([
  'context',
  'formState',
  'topology',
]);

const warnedReservedNames = new Set<string>();

/**
 * Build the interpolation context handed to `fetchDataSource`.
 * Shape: `{ <sourceName>: <resolvedData>, context, formState, topology }`.
 * Unresolved sources expose `undefined` — `getByPath` handles those
 * silently (missing paths resolve to `undefined`).
 *
 * Built-in roots win over `liveData`: spreading `liveData` first and
 * the reserved roots second guarantees `{{context.*}}`,
 * `{{formState.*}}`, and `{{topology.*}}` always resolve to the
 * host-supplied values, even if a misconfigured DSL declares a source
 * with one of those names. We additionally warn-once per reserved name
 * so the collision is visible in dev.
 */
function buildContextFromLiveData(
  flowContext: Readonly<Record<string, unknown>>,
  formState: FormState,
  liveData: Readonly<Record<string, unknown>>,
  route: CeRoute | undefined,
  scope: Readonly<Record<string, unknown>> | undefined,
  topology: TopologyProjection,
  endpointExcludedFormKeys?: ReadonlySet<string>,
): Record<string, unknown> {
  for (const name of Object.keys(liveData)) {
    const overridden =
      RESERVED_INTERPOLATION_ROOTS.has(name) ||
      (name === 'route' && route !== undefined) ||
      (name === 'scope' && scope !== undefined);
    if (overridden && !warnedReservedNames.has(name)) {
      warnedReservedNames.add(name);
      // eslint-disable-next-line no-console
      console.warn(
        `[DataSourceManager] data source name "${name}" shadows the built-in interpolation root and will be ignored. Rename the source.`,
      );
    }
  }
  const merged: Record<string, unknown> = {
    ...liveData,
    context: flowContext,
    formState,
    topology,
  };
  if (route !== undefined) {
    merged.route = route;
    markInjectedRouteContext(merged);
  }
  if (scope !== undefined) {
    merged.scope = scope;
    markPublishedScopeContext(merged);
  }
  return withEndpointExcludedFormKeys(merged, endpointExcludedFormKeys);
}

function serializeInjectedRoute(route: CeRoute | undefined): string {
  return stableStringify(route ?? null);
}

/**
 * Node-owned sources append the scope they were registered with. Two nodes
 * whose templates resolve to the same body under different overlays must not
 * share a cache entry; identical template and scope still do.
 */
function appendNodeScopeCacheSuffix(
  cacheKey: string,
  scope: Readonly<Record<string, unknown>> | undefined,
): string {
  if (scope === undefined) return cacheKey;
  return `${cacheKey} scope:${scopeContentSignature(scope)}`;
}

function affectedSourcesByRoute(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  orderedNames: readonly string[],
): string[] {
  return affectedSourcesByTemplateNeedle(dataSources, orderedNames, 'route');
}

function affectedSourcesByTemplateNeedle(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  orderedNames: readonly string[],
  needle: string,
): string[] {
  const direct = new Set<string>();
  for (const name of orderedNames) {
    const decl = dataSources[name];
    if (decl && declarationReferencesTemplate(decl, needle)) {
      direct.add(name);
    }
  }

  const affected = new Set<string>(direct);
  let grew = true;
  while (grew) {
    grew = false;
    for (const name of orderedNames) {
      if (affected.has(name)) continue;
      const deps = dataSources[name]?.dependsOn ?? [];
      for (const dep of deps) {
        if (affected.has(dep)) {
          affected.add(name);
          grew = true;
          break;
        }
      }
    }
  }

  return orderedNames.filter((name) => affected.has(name));
}

function declarationReferencesTemplate(
  decl: DataSourceDeclaration,
  root: string,
): boolean {
  return getDeclarationInterpolatableFields(decl).some((value) =>
    valueReferencesRoot(value, root),
  );
}

function valueReferencesRoot(value: unknown, root: string): boolean {
  if (typeof value === 'string') {
    if (!hasTemplate(value)) {
      return false;
    }
    const prefix = `${root}.`;
    try {
      return extractPaths(value).some(
        (path) => path === root || path.startsWith(prefix),
      );
    } catch {
      return false;
    }
  }
  if (Array.isArray(value)) {
    return value.some((item) => valueReferencesRoot(item, root));
  }
  if (value !== null && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((item) =>
      valueReferencesRoot(item, root),
    );
  }
  return false;
}

/**
 * Return the list of source names (in topological order) whose
 * declaration references `formState.<key>`, plus any sources transitively
 * dependent on those via `dependsOn`.
 */
function affectedSourcesByFormStateKey(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  orderedNames: readonly string[],
  key: string,
): string[] {
  const direct = new Set<string>();
  for (const name of orderedNames) {
    const decl = dataSources[name];
    if (!decl) continue;
    if (declarationReferencesFormKey(decl, key)) {
      direct.add(name);
    }
  }

  const affected = new Set<string>(direct);
  let grew = true;
  while (grew) {
    grew = false;
    for (const name of orderedNames) {
      if (affected.has(name)) continue;
      const deps = executionDependencyNames(dataSources[name]);
      for (const dep of deps) {
        if (affected.has(dep)) {
          affected.add(name);
          grew = true;
          break;
        }
      }
    }
  }

  return orderedNames.filter((name) => affected.has(name));
}

/**
 * `names` plus every source that transitively waits on one of them, in
 * topological order.
 */
function withExecutionDependents(
  dataSources: Readonly<Record<string, DataSourceDeclaration>>,
  orderedNames: readonly string[],
  names: readonly string[],
): string[] {
  const affected = new Set<string>(names);
  let grew = true;
  while (grew) {
    grew = false;
    for (const name of orderedNames) {
      if (affected.has(name)) continue;
      if (
        executionDependencyNames(dataSources[name]).some((dep) =>
          affected.has(dep),
        )
      ) {
        affected.add(name);
        grew = true;
      }
    }
  }
  return orderedNames.filter((name) => affected.has(name));
}

/**
 * Return true when any interpolatable field of `decl` reads
 * `formState.<key>`, including a quoted key such as `formState["q"]`.
 */
function declarationReferencesFormKey(
  decl: DataSourceDeclaration,
  key: string,
): boolean {
  if (declarationReadsBoundFormKey(decl, key)) {
    return true;
  }
  const haystacks = getDeclarationInterpolatableFields(decl);
  if (!haystacks.some((value) => hasTemplateDeep(value))) {
    return false;
  }
  return deepReferencesFormKey(haystacks, key);
}

function deepReferencesFormKey(value: unknown, key: string): boolean {
  if (typeof value === 'string') {
    return stringReferencesFormKey(value, key);
  }
  if (Array.isArray(value)) {
    return value.some((item) => deepReferencesFormKey(item, key));
  }
  if (value !== null && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).some((item) =>
      deepReferencesFormKey(item, key),
    );
  }
  return false;
}

function stringReferencesFormKey(template: string, key: string): boolean {
  if (!template.includes('{{')) return false;
  if (template.includes(`formState.${key}`)) return true;
  try {
    return parse(template).segments.some(
      (segment) =>
        segment.type === 'expression' && expressionReadsFormKey(segment, key),
    );
  } catch {
    return false;
  }
}

function expressionReadsFormKey(expression: Expression, key: string): boolean {
  const root = expression.segments[0];
  const field = expression.segments[1];
  if (
    root?.type === 'key' &&
    root.name === 'formState' &&
    field?.type === 'key' &&
    field.name === key
  ) {
    return true;
  }
  return (
    expression.arg !== undefined && expressionReadsFormKey(expression.arg, key)
  );
}

function isEmpty(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length === 0;
  }
  return false;
}

function readFlowType(
  flowContext: Readonly<Record<string, unknown>>,
): string | undefined {
  const value = flowContext.flowType;
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function appendFlowTypeCacheSuffix(
  cacheKey: string,
  flowType: unknown,
): string {
  const normalized = readFlowType({ flowType });
  return normalized ? `${cacheKey}:flowType=${normalized}` : cacheKey;
}

/**
 * Read the effective traffic source for Offerings requests. Step-configured
 * `context.trafficSource` wins over the landing URL's `utm_cap` value.
 */
function readTrafficSource(
  flowContext: Readonly<Record<string, unknown>>,
): string | undefined {
  const contextValue = flowContext.trafficSource;
  if (typeof contextValue === 'string' && contextValue.length > 0) {
    return contextValue;
  }
  // `typeof` guard rather than `window?.` — optional chaining still throws a
  // ReferenceError on an undeclared `window` (SSR/build), it only guards
  // null/undefined values.
  if (typeof window === 'undefined') {
    return undefined;
  }
  const value = getTrafficSource(window.location?.href ?? '');
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function appendTrafficSourceCacheSuffix(
  cacheKey: string,
  trafficSource: string | undefined,
): string {
  return trafficSource
    ? `${cacheKey}:trafficSource=${trafficSource}`
    : cacheKey;
}

function waitsOn(
  decl: DataSourceDeclaration,
  upstream: ReadonlySet<string>,
): boolean {
  return executionDependencyNames(decl).some((name) => upstream.has(name));
}

function authorizationKey(
  tenantId: string | undefined,
  trustedOrigins: readonly string[] | undefined,
): string {
  return `${tenantId ?? ''}\u0000${(trustedOrigins ?? []).join('\u0000')}`;
}

/**
 * Read the current page URL to inject as `presentationUrl` into flat
 * Offerings `/search` POST bodies, so offerings-service can apply page-level
 * restrictions. Returns `undefined` when there is no window (SSR) or the URL
 * is empty, so no field is injected in those cases.
 */
function readPresentationUrl(): string | undefined {
  // `typeof` guard rather than `window?.` — optional chaining still throws a
  // ReferenceError on an undeclared `window` (SSR/build), it only guards
  // null/undefined values.
  if (typeof window === 'undefined') {
    return undefined;
  }
  const href = window.location?.href ?? '';
  return href.length > 0 ? href : undefined;
}

function appendPresentationUrlCacheSuffix(
  cacheKey: string,
  presentationUrl: string | undefined,
): string {
  return presentationUrl
    ? `${cacheKey}:presentationUrl=${presentationUrl}`
    : cacheKey;
}
